import { create } from "zustand";
import { persist, createJSONStorage, devtools } from "zustand/middleware";
import CryptoJS from "crypto-js";
import { removeCookie } from "@/utils/cookie";
import { usePreferencesStore } from "./usePreferencesStore";

const SECRET_KEY =
  "4226452948404D635166546A576D5A7134743777217A25432A462D4A614E6452";

// Custom storage with encryption
const encryptedStorage = {
  getItem: (name) => {
    const str = localStorage.getItem(name);
    if (!str) return null;

    try {
      const decrypted = CryptoJS.AES.decrypt(str, SECRET_KEY).toString(
        CryptoJS.enc.Utf8,
      );
      return decrypted ? JSON.parse(decrypted) : null;
    } catch (error) {
      console.error("Decryption error:", error);
      return null;
    }
  },
  setItem: (name, value) => {
    try {
      const encrypted = CryptoJS.AES.encrypt(
        JSON.stringify(value),
        SECRET_KEY,
      ).toString();
      localStorage.setItem(name, encrypted);
    } catch (error) {
      console.error("Encryption error:", error);
    }
  },
  removeItem: (name) => {
    localStorage.removeItem(name);
  },
};

const initialState = {
  isLoggedIn: false,
  email: null,
  user: null,
  otpFlow: null, // 'login', 'signup', 'forgot-password'
  pendingCircleInviteOwnerUserId: null,
};

export const useAuthStore = create(
  devtools(
    persist(
      (set) => ({
        ...initialState,

        setEmail: (email) => set({ email }, false, "setEmail"),

        clearEmail: () => set({ email: null }, false, "clearEmail"),

        setUser: (user) =>
          set(
            {
              user,
              isLoggedIn: true,
            },
            false,
            "setUser",
          ),

        updateUser: (updates) =>
          set(
            (state) => {
              const nextUser =
                typeof updates === "function"
                  ? updates(state.user)
                  : { ...(state.user || {}), ...(updates || {}) };

              return {
                user: nextUser,
                isLoggedIn: !!nextUser,
              };
            },
            false,
            "updateUser",
          ),

        clearUser: () =>
          set(
            {
              user: null,
              isLoggedIn: false,
            },
            false,
            "clearUser",
          ),

        setOtpFlow: (otpFlow) => set({ otpFlow }, false, "setOtpFlow"),

        clearOtpFlow: () => set({ otpFlow: null }, false, "clearOtpFlow"),

        setPendingCircleInviteOwnerUserId: (pendingCircleInviteOwnerUserId) =>
          set(
            { pendingCircleInviteOwnerUserId },
            false,
            "setPendingCircleInviteOwnerUserId",
          ),

        clearPendingCircleInviteOwnerUserId: () =>
          set(
            { pendingCircleInviteOwnerUserId: null },
            false,
            "clearPendingCircleInviteOwnerUserId",
          ),

        logout: () => {
          removeCookie("access_token");
          removeCookie("terms_accepted");
          removeCookie("user_role");
          usePreferencesStore.getState().clearPreferences();
          set(initialState, false, "logout");
        },

        // Clear all persisted auth data (including from storage)
        clearAll: () => {
          set(initialState, false, "clearAll");
          // Force clear the persisted storage
          try {
            localStorage.removeItem("auth");
          } catch (error) {
            console.error("Error clearing auth storage:", error);
          }
        },
      }),
      {
        name: "auth",
        // Use plain localStorage in development for Redux DevTools compatibility
        // Use encrypted storage in production for security
        storage: createJSONStorage(() =>
          process.env.NODE_ENV === "development"
            ? localStorage
            : encryptedStorage,
        ),
      },
    ),
    {
      name: "AuthStore",
    },
  ),
);

// Selectors (custom hooks for easier migration from Redux selectors)
export const useAuthSelector = () => useAuthStore((state) => state);
export const useEmailSelector = () => useAuthStore((state) => state.email);
export const useUserSelector = () => useAuthStore((state) => state.user);
export const useOtpFlowSelector = () => useAuthStore((state) => state.otpFlow);
export const usePendingCircleInviteOwnerUserIdSelector = () =>
  useAuthStore((state) => state.pendingCircleInviteOwnerUserId);
